import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { getLocalDateStr, getPuzzleForDate } from '../utils/dailyPuzzleGen'
import { getDailyCompletionForDate } from '../utils/storage'
import { getPersonWithCredits, profileUrl, posterUrl } from '../api/tmdb'
import { formatPlaceOfBirth } from '../utils/placeOfBirth'
import { IconX } from './icons'

// ── Countdown helpers ─────────────────────────────────────────────────────────

function getMsUntilMidnight() {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)
  return tomorrow - now
}

function formatCountdown() {
  const total = Math.max(0, Math.floor(getMsUntilMidnight() / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const PLAY_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
)
const CHECK_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
)
const CLOCK_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" /></svg>
)

// ── Today's Spotlight helpers ─────────────────────────────────────────────────

function formatBirthday(dateStr) {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3C/svg%3E"

// Top 3 "Known For" films, derived from the already-fetched movie_credits
// (TMDB's /person/{id} detail endpoint doesn't carry a `known_for` array — that
// only appears on search/popular results — so we rank the credited cast by
// popularity, which is the standard way to surface an actor's best-known work).
function topKnownFor(details) {
  const seen = new Set()
  return (details?.movie_credits?.cast ?? [])
    .filter((m) => m.poster_path && m.title && !m.adult)
    .filter((m) => { if (seen.has(m.id)) return false; seen.add(m.id); return true })
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
    .slice(0, 3)
}

// ── Goal-actor bio flip card (modal) ──────────────────────────────────────────
// Opened by tapping the goal actor's circular headshot. Flips + zooms in over a
// dimmed backdrop. Rendered via a portal so it's centered on the viewport and
// (being a React sibling of the clickable daily card, not a descendant) its
// click events never bubble back to the card's launch handler.
function GoalActorBioModal({ actor, onClose }) {
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [closing, setClosing] = useState(false)

  // Lazy-load /person/{id} (+ movie_credits) on open. The home screen's daily
  // card already fetched this for the spotlight, so the in-memory cache usually
  // makes this instant; otherwise it fetches now.
  useEffect(() => {
    let cancelled = false
    getPersonWithCredits(actor.id)
      .then((data) => { if (!cancelled) { setDetails(data); setLoading(false) } })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [actor.id])

  // Lock background scroll + wire Escape-to-close while open.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') setClosing(true) }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  // When the exit animation finishes, actually unmount.
  useEffect(() => {
    if (!closing) return
    const id = setTimeout(onClose, 270)
    return () => clearTimeout(id)
  }, [closing, onClose])

  const bigImg   = actor.profile_path ? profileUrl(actor.profile_path, 'w500') : ACTOR_PH
  const knownFor = topKnownFor(details)

  return createPortal(
    <div
      className={`bio-modal__backdrop${closing ? ' bio-modal__backdrop--closing' : ''}`}
      onClick={() => setClosing(true)}
      role="dialog"
      aria-modal="true"
      aria-label={`${actor.name} — biography`}
    >
      <div
        className={`bio-modal__card${closing ? ' bio-modal__card--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="bio-modal__close" onClick={() => setClosing(true)} aria-label="Close">
          <IconX size={16} />
        </button>

        <img className="bio-modal__photo" src={bigImg} alt={actor.name} draggable={false} />

        <div className="bio-modal__body">
          <h3 className="bio-modal__name">{actor.name}</h3>

          {knownFor.length > 0 && (
            <div className="bio-modal__known-wrap">
              <p className="bio-modal__label">Known For</p>
              <div className="bio-modal__known">
                {knownFor.map((m) => (
                  <div key={m.id} className="bio-modal__known-item">
                    <img src={posterUrl(m.poster_path, 'w185')} alt={m.title} draggable={false} />
                    <span className="bio-modal__known-title">{m.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <p className="bio-modal__bio bio-modal__bio--empty">Loading…</p>
          ) : details?.biography ? (
            <p className="bio-modal__bio">{details.biography}</p>
          ) : (
            <p className="bio-modal__bio bio-modal__bio--empty">No biography available.</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

// Actor-pair connection: two circular portraits joined by a dotted connector.
// Center node is a ringed "?" (not completed) or a solid diamond (completed).
// Only the goal (end) actor's portrait is tappable — it opens the bio modal.
function ActorPairConnection({ startActor, endActor, completed, onGoalTap }) {
  const Avatar = ({ actor, onTap }) => {
    const src = actor?.profile_path ? profileUrl(actor.profile_path, 'w185') : ACTOR_PH
    const inner = (
      <>
        <img className="daily-pair__avatar" src={src} alt={actor?.name || ''} draggable={false} />
        <span className="daily-pair__name">{actor?.name || '…'}</span>
      </>
    )
    // Tappable only once the goal actor has loaded. stopPropagation so the tap
    // opens the bio modal instead of launching the daily challenge.
    if (onTap && actor?.id) {
      return (
        <button
          type="button"
          className="daily-pair__actor daily-pair__actor--tappable"
          onClick={(e) => { e.stopPropagation(); onTap() }}
          aria-label={`${actor?.name || 'Goal actor'} — view bio`}
        >
          {inner}
        </button>
      )
    }
    return <div className="daily-pair__actor">{inner}</div>
  }
  return (
    <div className="daily-pair">
      <Avatar actor={startActor} />
      <div className="daily-pair__connector">
        <span className={`daily-pair__node${completed ? ' daily-pair__node--done' : ' daily-pair__node--open'}`}>
          {completed ? <span className="daily-pair__diamond" aria-hidden="true" /> : '?'}
        </span>
      </div>
      <Avatar actor={endActor} onTap={onGoalTap} />
    </div>
  )
}

// Goal-actor bio section. Headed by a "Today's Goal Actor" label (styled like
// the card's "Today's Puzzle" title) and tappable anywhere to open the same bio
// modal as the puzzle-section goal headshot. Front is hint-neutral (Place of
// Birth, not films).
function SpotlightInner({ actor, onTap }) {
  if (!actor) return null
  const imgSrc = actor.profile_path ? profileUrl(actor.profile_path, 'w185') : ACTOR_PH
  const placeOfBirth = formatPlaceOfBirth(actor.placeOfBirth)

  // The enclosing .daily-card has its own onClick (launches the challenge), so
  // stop propagation — a tap here opens the bio modal instead of starting play.
  const tap = (e) => { e.stopPropagation(); onTap?.() }
  const key = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onTap?.() }
  }

  return (
    <>
      <div className="home__daily-divider" aria-hidden="true" />
      <div
        className="home__daily-goal"
        onClick={onTap ? tap : undefined}
        onKeyDown={onTap ? key : undefined}
        role={onTap ? 'button' : undefined}
        tabIndex={onTap ? 0 : undefined}
        aria-label={onTap ? `${actor.name} — view bio` : undefined}
      >
        <span className="daily-card__title home__daily-goal-label">Today's Goal Actor</span>
        <span className="home__daily-goal-hint">Tap for details</span>
        <div className="home__daily-spotlight">
          <img
            className="spotlight__photo"
            src={imgSrc}
            alt={actor.name}
            draggable={false}
          />
          <div className="spotlight__info">
            <p className="spotlight__name">{actor.name}</p>
            {actor.birthday && (
              <p className="spotlight__detail">
                <span className="spotlight__detail-key">Born</span>
                {formatBirthday(actor.birthday)}
              </p>
            )}
            {placeOfBirth && (
              <p className="spotlight__detail">
                <span className="spotlight__detail-key">Place of Birth</span>
                {placeOfBirth}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Reusable Daily Challenge card. Self-contained: loads today's puzzle pair, the
 * goal-actor spotlight details, completion state, and the next-puzzle countdown.
 *
 * Props:
 *   onActivate — called when the card (or its Play/check button) is tapped.
 *   bottom     — optional node rendered in the card's bottom region instead of the
 *                default actor-details spotlight (e.g. the Single Player screen
 *                passes a "Play Past Puzzles" sub-card here). Home omits it to get
 *                the spotlight.
 */
export default function DailyCard({ onActivate, bottom }) {
  const todayStr = getLocalDateStr()

  const completionData = getDailyCompletionForDate(todayStr)
  const isCompleted    = !!completionData

  const [puzzlePair, setPuzzlePair]         = useState(null)   // { startActor, endActor }
  const [spotlightActor, setSpotlightActor] = useState(null)   // goal actor + details
  const [countdown, setCountdown]           = useState(formatCountdown)
  const [bioOpen, setBioOpen]               = useState(false)  // goal-actor bio modal

  // Single shared opener — used by BOTH the puzzle-section goal headshot and the
  // bottom bio section so they trigger the exact same modal (no duplicated logic).
  const openBio = () => setBioOpen(true)

  // Load today's endpoint actor pair (for the connection portraits) + the
  // goal-actor spotlight details (birthday + place of birth).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const pair = await getPuzzleForDate(todayStr)
        if (cancelled || !pair) return
        setPuzzlePair({ startActor: pair.startActor, endActor: pair.endActor })

        const goal = pair.endActor
        if (!goal?.id) return
        setSpotlightActor({ name: goal.name, profile_path: goal.profile_path })
        const details = await getPersonWithCredits(goal.id)
        if (cancelled) return
        setSpotlightActor({
          name:         goal.name,
          profile_path: goal.profile_path,
          birthday:     details.birthday        ?? null,
          placeOfBirth: details.place_of_birth  ?? null,
        })
      } catch {
        // Non-critical enhancement — fail silently
      }
    })()
    return () => { cancelled = true }
  }, [todayStr])

  // Countdown ticker (completed state)
  useEffect(() => {
    if (!isCompleted) return
    const id = setInterval(() => setCountdown(formatCountdown()), 1000)
    return () => clearInterval(id)
  }, [isCompleted])

  return (
    <>
      <div
        className={`daily-card${isCompleted ? ' daily-card--completed' : ''}`}
        onClick={() => onActivate?.()}
        style={{ cursor: 'pointer' }}
      >
        <div className="daily-card__title-row">
          <span className="daily-card__title">
            {isCompleted ? "Today's Puzzle Complete!" : "Today's Puzzle"}
          </span>
          <button
            type="button"
            className="daily-card__btn"
            onClick={(e) => { e.stopPropagation(); onActivate?.() }}
            aria-label={isCompleted ? "View today's completed puzzle" : 'Play daily challenge'}
          >
            {isCompleted ? CHECK_ICON : PLAY_ICON}
          </button>
        </div>

        <ActorPairConnection
          startActor={puzzlePair?.startActor}
          endActor={puzzlePair?.endActor}
          completed={isCompleted}
          onGoalTap={openBio}
        />

        {isCompleted && (
          <div className="daily-card__countdown">
            {CLOCK_ICON}
            <span>Next puzzle in {countdown}</span>
          </div>
        )}

        {bottom !== undefined ? bottom : <SpotlightInner actor={spotlightActor} onTap={openBio} />}
      </div>

      {bioOpen && puzzlePair?.endActor && (
        <GoalActorBioModal
          actor={puzzlePair.endActor}
          onClose={() => setBioOpen(false)}
        />
      )}
    </>
  )
}
